// Inline ticket-thread renderer. Mounts inside an expanded TableRow on
// the Support list page; fetches the full message thread on demand via a
// per-ticket queryKey so collapsing + re-expanding hits the cache.
//
// Two interaction surfaces:
//   • "Add reply" textarea + button → POST /v1/support/:id/messages.
//     Re-opens a `waiting_customer` / `resolved` ticket server-side
//     (handled by appendMessage in the repo).
//   • "Mark as closed" button (only when status === 'resolved') → PATCH
//     /v1/support/:id with { acknowledgeResolution: true }.
//
// The mutations both invalidate the per-ticket detail key AND the list
// key so the row's status badge + last-activity timestamp track.

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import type { SupportMessage } from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { friendlyError } from '../../lib/error-message.js';
import { formatDateTime } from '../../lib/format-date.js';
import {
  supportApi,
  SUPPORT_QUERY_KEY,
  supportTicketQueryKey,
} from '../../lib/api/support.js';
import { Button } from '../../components/ui/button.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { Textarea } from '../../components/ui/textarea.js';
import { toast } from '../../components/ui/toaster.js';
import { cn } from '../../lib/utils.js';

interface TicketThreadProps {
  id: string;
}

export function TicketThread({ id }: TicketThreadProps) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');

  const detail = useQuery({
    queryKey: supportTicketQueryKey(id),
    queryFn: () => supportApi.get(api, id),
  });

  const addReply = useMutation({
    mutationFn: () => supportApi.addMessage(api, id, { body: reply.trim() }),
    onSuccess: () => {
      setReply('');
      void queryClient.invalidateQueries({ queryKey: supportTicketQueryKey(id) });
      // List query carries last_message_at + status — invalidate so the
      // row badge updates and the activity timestamp moves the row to
      // the top.
      void queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEY });
    },
    onError: (err) => {
      toast.error('Could not send reply', {
        description: friendlyError(err, 'Try again in a moment.'),
      });
    },
  });

  const acknowledge = useMutation({
    mutationFn: () =>
      supportApi.update(api, id, { acknowledgeResolution: true }),
    onSuccess: () => {
      toast.success('Ticket closed');
      void queryClient.invalidateQueries({ queryKey: supportTicketQueryKey(id) });
      void queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEY });
    },
    onError: (err) => {
      toast.error('Could not close ticket', {
        description: friendlyError(err, 'Try again in a moment.'),
      });
    },
  });

  if (detail.isLoading) return <ThreadSkeleton />;
  if (detail.isError) {
    return (
      <ErrorRetry
        title="Could not load this ticket"
        error={detail.error}
        onRetry={() => void detail.refetch()}
      />
    );
  }
  if (!detail.data) return null;

  const { ticket, messages } = detail.data;
  const isClosed = ticket.status === 'closed';
  const isResolved = ticket.status === 'resolved';

  function onReplySubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (reply.trim().length === 0) return;
    addReply.mutate();
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
      </div>

      {isResolved && (
        <div className="flex items-start gap-3 rounded-md border border-success/40 bg-success/5 p-3 text-sm">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-success" aria-hidden />
          <div className="flex-1">
            <p className="font-medium">Marked as resolved</p>
            <p className="text-muted-foreground">
              If this fixed it, close the ticket. If not, replying will re-open it.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { acknowledge.mutate(); }}
            disabled={acknowledge.isPending}
          >
            {acknowledge.isPending ? 'Closing…' : 'Mark as closed'}
          </Button>
        </div>
      )}

      {!isClosed && (
        <form className="space-y-2" onSubmit={onReplySubmit}>
          <Textarea
            value={reply}
            onChange={(e) => { setReply(e.target.value); }}
            rows={3}
            placeholder="Add a reply…"
            maxLength={10_000}
            aria-label="Reply to ticket"
          />
          <div className="flex items-center justify-end">
            <Button
              type="submit"
              size="sm"
              disabled={reply.trim().length === 0 || addReply.isPending}
            >
              {addReply.isPending ? 'Sending…' : 'Send reply'}
            </Button>
          </div>
        </form>
      )}

      {isClosed && (
        <p className="text-sm text-muted-foreground">
          This ticket is closed. File a new ticket to continue the conversation.
        </p>
      )}
    </div>
  );
}

function MessageBubble({ message }: { message: SupportMessage }) {
  const isStaff = message.authorKind === 'staff';
  return (
    <div
      className={cn(
        'rounded-md border p-3 text-sm',
        isStaff ? 'border-primary/30 bg-primary/5' : 'bg-card',
      )}
    >
      <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">
          {isStaff ? 'Cortex support' : 'You'}
        </span>
        <time dateTime={message.createdAt}>
          {formatDateTime(message.createdAt)}
        </time>
      </div>
      <p className="whitespace-pre-wrap">{message.body}</p>
    </div>
  );
}

function ThreadSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="rounded-md border p-3">
          <Skeleton className="mb-2 h-3 w-20" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="mt-1 h-4 w-3/4" />
        </div>
      ))}
    </div>
  );
}
